import React, { createContext, useContext, useEffect, useState } from "react";
import {
  EMPYREAN_CREATE_API,
  EMPYREAN_LIST_API,
  EMPYREAN_UPDATE_API,
  EMPYREAN_DELETE_API,
  EMPYREAN_GET_MODEL_DATABASE_CP_DATES_API,
} from "../config/apiConfig";
import { securedFetch } from "../utils/api";

const EmpyreanContext = createContext();

const createRow = (row = {}) => ({
  businessEntityID: row.businessEntityID || "",
  modelDatabase: row.modelDatabase || "",
  monthEnd: row.monthEnd || "",
  advancedNII: row.advancedNII ?? false,
  eveMethodEmpyrean: row.eveMethodEmpyrean ?? false,
  treasuryKRIGlobalView: row.treasuryKRIGlobalView ?? false,
  isPersisted: row.isPersisted ?? false,
  isEditing: row.isEditing ?? false,
  labelError: null,
});

export const EmpyreanProvider = ({ children }) => {
  const [rows, setRows] = useState([]);
  const [modelDatabaseGroups, setModelDatabaseGroups] = useState([]);
  const [empyreanListLoading, setEmpyreanListLoading] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setEmpyreanListLoading(true);

      const [listResp, groupResp] = await Promise.all([
        securedFetch(EMPYREAN_LIST_API, { method: "GET" }),
        securedFetch(EMPYREAN_GET_MODEL_DATABASE_CP_DATES_API, {
          method: "GET",
        }),
      ]);

      if (!listResp.ok) throw new Error("Failed to load list");
      if (!groupResp.ok)
        throw new Error("Failed to load model database groups");

      const listData = await listResp.json();
      const groupData = await groupResp.json();

      setModelDatabaseGroups(groupData);

      const initialRows = listData.map((item) =>
        createRow({
          businessEntityID: item.businessEntityID,
          modelDatabase: item.modelDatabase || item.modelName,
          monthEnd: item.monthEnd,
          advancedNII: item.advancedNII,
          eveMethodEmpyrean: item.eveMethodEmpyrean,
          treasuryKRIGlobalView: item.treasuryKRIGlobalView,
          isPersisted: true,
          isEditing: false,
        }),
      );

      setRows(initialRows);
    } catch (err) {
      console.error(err);
      setRows([]);
      setModelDatabaseGroups([]);
    } finally {
      setEmpyreanListLoading(false);
    }
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      createRow({
        isPersisted: false,
        isEditing: true,
      }),
    ]);
  };

  const updateRow = (index, changes) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...changes } : row)),
    );
  };

  const removeRow = async (index) => {
    const row = rows[index];
    if (!row) return;

    if (row.isPersisted) {
      const resp = await securedFetch(
        EMPYREAN_DELETE_API(encodeURIComponent(row.modelDatabase)),
        { method: "DELETE" },
      );
      if (resp.ok) {
        alert("Record deleted successfully");
      } else {
        const errorMessage = await resp.text();
        alert(errorMessage);
      }

      if (!resp.ok) throw new Error("Delete failed");
    }

    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const saveNewRow = async (index) => {
    const row = rows[index];

    const payload = {
      businessEntityID: row.businessEntityID,
      modelDatabase: row.modelDatabase,
      monthEnd: row.monthEnd,
      advancedNII: row.advancedNII,
      eveMethodEmpyrean: row.eveMethodEmpyrean,
      treasuryKRIGlobalView: row.treasuryKRIGlobalView,
    };

    try {
      const response = await securedFetch(EMPYREAN_CREATE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert("Record created successfully");
      } else {
        const errorMessage = await response.text();
        alert(errorMessage);
      }
    } catch (error) {
      alert(error.message);
    }

    await loadInitialData();
  };

  const updateExistingRow = async (index) => {
    const row = rows[index];

    const payload = {
      modelDatabase: row.modelDatabase,
      businessEntityID: row.businessEntityID,
      monthEnd: row.monthEnd,
      advancedNII: row.advancedNII,
      eveMethodEmpyrean: row.eveMethodEmpyrean,
      treasuryKRIGlobalView: row.treasuryKRIGlobalView,
    };

    try {
      const response = await securedFetch(EMPYREAN_UPDATE_API, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert("Record updated successfully");
      } else {
        const errorMessage = await response.text();
        alert(errorMessage);
      }
    } catch (error) {
      alert(error.message);
    }

    await loadInitialData();
  };

  const getBusinessEntityOptions = () =>
    modelDatabaseGroups.map((item) => item.businessEntityID);

  const getModelDatabaseOptions = () =>
    modelDatabaseGroups.flatMap((item) =>
      (item.modelInfo || []).map((model) => ({
        businessEntityID: item.businessEntityID,
        modelDatabase: model.modelDatabase,
        cpDate: model.cpDate,
      })),
    );

  return (
    <EmpyreanContext.Provider
      value={{
        rows,
        empyreanListLoading,
        addRow,
        updateRow,
        removeRow,
        saveNewRow,
        updateExistingRow,
        getBusinessEntityOptions,
        getModelDatabaseOptions,
        modelDatabaseGroups,
        loadInitialData,
      }}
    >
      {children}
    </EmpyreanContext.Provider>
  );
};

export const useEmpyreanContext = () => useContext(EmpyreanContext);
